import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { BellRing, Check, Loader2, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Polls /api/admin/waiter-calls every 8s, shows pending calls as a banner.
 * Plays a SCHOOL-BELL style alert (multi-tone bell + repeated rings) on new calls.
 */
const WaiterCallsBanner = ({ token, embedded = false }) => {
  const [calls, setCalls] = useState([]);
  const [acking, setAcking] = useState(null);
  const [lastIds, setLastIds] = useState(new Set());
  const [muted, setMuted] = useState(() => localStorage.getItem('waiterBellMuted') === 'true');
  const audioCtxRef = useRef(null);

  // Lazily create one AudioContext (must be resumed after a user gesture)
  const getCtx = () => {
    if (!audioCtxRef.current) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return null;
      audioCtxRef.current = new AudioCtx();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  };

  /**
   * Old-school SCHOOL BELL synthesizer.
   * Layers: fundamental + 3 inharmonic partials + a soft "clang" noise burst,
   * each with an exponential decay. Plays 3 rings (ding-ding-ding) ~0.5s apart.
   */
  const playSchoolBell = () => {
    const ctx = getCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const ringTimes = [0, 0.5, 1.0]; // 3 successive rings

    const master = ctx.createGain();
    master.gain.value = 0.35;
    master.connect(ctx.destination);

    ringTimes.forEach((t0) => {
      const start = now + t0;
      // Bell-like inharmonic partials (relative to ~880 Hz fundamental)
      const partials = [
        { freq: 880, gain: 0.55 },
        { freq: 1320, gain: 0.4 },
        { freq: 1760, gain: 0.25 },
        { freq: 2640, gain: 0.15 },
      ];
      partials.forEach(({ freq, gain }) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        // Quick attack + long-ish exponential decay (bell shimmer)
        g.gain.setValueAtTime(0.0001, start);
        g.gain.exponentialRampToValueAtTime(gain, start + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, start + 0.45);
        osc.connect(g).connect(master);
        osc.start(start);
        osc.stop(start + 0.5);
      });
      // Tiny clang noise burst for the "hammer hit" feel
      try {
        const bufferSize = Math.floor(ctx.sampleRate * 0.03);
        const noiseBuf = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = noiseBuf.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
        const src = ctx.createBufferSource();
        src.buffer = noiseBuf;
        const ng = ctx.createGain();
        ng.gain.value = 0.18;
        src.connect(ng).connect(master);
        src.start(start);
      } catch { /* noise optional */ }
    });
  };

  const fetchCalls = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/waiter-calls?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const items = res.data || [];
      const newIds = items.map(c => c.id);
      const fresh = newIds.filter(id => !lastIds.has(id));
      if (lastIds.size > 0 && fresh.length > 0) {
        if (!muted) playSchoolBell();
        const newest = items.find(c => c.id === fresh[0]);
        if (newest) toast.info(`🔔 Masa ${newest.table_number} garson çağırdı`, { duration: 6000 });
      }
      setLastIds(new Set(newIds));
      setCalls(items);
    } catch {
      // silent
    }
  }, [token, lastIds, muted]);

  useEffect(() => {
    fetchCalls();
    const interval = setInterval(fetchCalls, 8000);
    return () => clearInterval(interval);
  }, [fetchCalls]);

  const acknowledge = async (id) => {
    setAcking(id);
    try {
      await axios.put(`${API}/admin/waiter-calls/${id}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalls(prev => prev.filter(c => c.id !== id));
      toast.success('Çağrı onaylandı');
    } catch {
      toast.error('Onaylanamadı');
    } finally {
      setAcking(null);
    }
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem('waiterBellMuted', String(next));
    if (!next) {
      // Unmuting → user gesture, prime audio + preview
      playSchoolBell();
      toast.success('Garson çağrı zili aktif');
    } else {
      toast.info('Garson çağrı zili sustu');
    }
  };

  const testBell = () => {
    getCtx();
    playSchoolBell();
  };

  // Always render the control bar (even with 0 calls) so the user can mute/test.
  const hasCalls = calls.length > 0;

  return (
    <div className={embedded ? 'mb-4' : 'mb-6'} data-testid="waiter-calls-banner">
      {/* Mute + Test controls */}
      <div className="flex items-center justify-end gap-1.5 mb-2">
        <button
          onClick={testBell}
          className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
          data-testid="bell-test-btn"
          title="Çağrı zilini test et"
        >
          🔔 Zili Test Et
        </button>
        <button
          onClick={toggleMute}
          className={`flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${
            muted ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
          }`}
          data-testid="bell-mute-toggle"
          title={muted ? 'Sesi aç' : 'Sesi kapat'}
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          {muted ? 'Zil Kapalı' : 'Zil Açık'}
        </button>
      </div>

      <AnimatePresence mode="popLayout">
        {hasCalls && calls.map((c) => {
          const elapsed = Math.floor((Date.now() - new Date(c.created_at).getTime()) / 1000);
          return (
            <motion.div
              key={c.id}
              layout
              initial={{ opacity: 0, y: -10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: 80, scale: 0.95 }}
              transition={{ type: 'spring', damping: 22, stiffness: 220 }}
              className="bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl px-4 py-3 shadow-lg shadow-amber-500/30 ring-1 ring-white/20 flex items-center justify-between gap-3 mb-2"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/30 flex-shrink-0">
                  <BellRing className="w-5 h-5 animate-pulse" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider opacity-80 leading-none">Garson Çağrısı</p>
                  <p className="font-bold text-lg leading-tight truncate">Masa {c.table_number}</p>
                  <p className="text-[11px] opacity-80">{elapsed < 60 ? `${elapsed} sn önce` : `${Math.floor(elapsed / 60)} dk önce`}</p>
                </div>
              </div>
              <button
                onClick={() => acknowledge(c.id)}
                disabled={acking === c.id}
                data-testid={`ack-call-${c.table_number}`}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-white text-orange-700 rounded-full font-semibold text-sm hover:bg-amber-50 active:scale-95 transition-all disabled:opacity-60"
              >
                {acking === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Geldim
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default WaiterCallsBanner;
